import { inject, Injectable } from '@angular/core';
import { from, merge, Observable, switchMap, timer } from 'rxjs';

import { NavController } from '@ionic/angular';

import { AuthenticationService } from '../../../shared/services/authentication.service';
import type { ConsentUpdatePayload, UserConsentItem } from './consent.rest.service';
import { ConsentRestService } from './consent.rest.service';

/**
 * Orchestration-level consent service for the menu feature.
 * Composes consent loading patterns that are too behavioural for the pure HTTP adapter.
 */
@Injectable({
  providedIn: 'root',
})
export class MenuConsentService {
  private readonly consentRestService = inject(ConsentRestService);
  private readonly authenticationService = inject(AuthenticationService);
  private readonly navController = inject(NavController);

  /**
   * Emits the user's consents immediately, then emits again after a 5-minute refresh.
   * The caller is responsible for flattening operator semantics (e.g. switchMap).
   */
  loadConsentsWithScheduledRefresh$(language: string): Observable<Array<UserConsentItem>> {
    return merge(
      this.consentRestService.loadUserConsents$(language),
      timer(5 * 60 * 1000).pipe(switchMap(() => this.consentRestService.loadUserConsents$(language)))
    );
  }

  /**
   * Submits the consent update to the backend, then handles the post-submit navigation:
   * - onboarding mode: navigate to home
   * - mandatory consent revoked: log out
   * Emits the mandatoryRevoked flag so the caller can record it in the succeeded action.
   */
  submitAndNavigate$(
    payload: Array<ConsentUpdatePayload>,
    language: string,
    mode: 'onboarding' | 'update',
    mandatoryRevoked: boolean
  ): Observable<{ mandatoryRevoked: boolean }> {
    return this.consentRestService.updateUserConsents$(payload, language).pipe(
      switchMap(() => {
        if (mode === 'onboarding') {
          return from(this.navController.navigateBack('/'));
        }
        if (mandatoryRevoked) {
          return this.authenticationService.logOut$();
        }
        return from(Promise.resolve());
      }),
      switchMap(() => [{ mandatoryRevoked }])
    );
  }
}
